import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Users, MessageSquare, Activity, AlertTriangle, Settings } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { ReportsTable } from './ReportsTable';
import { UserManagement } from './UserManagement';
import { SystemSettings } from './SystemSettings';
import { Analytics } from './Analytics';
import { cn } from '@/lib/utils';

interface Stats {
  totalUsers: number;
  activePosts: number;
  activeUsers: number;
  pendingReports: number;
}

export function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    activePosts: 0,
    activeUsers: 0,
    pendingReports: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'reports' | 'users' | 'settings' | 'analytics'>('overview');

  useEffect(() => {
    if (user) {
      checkAdminRole();
    }
  }, [user]);

  const checkAdminRole = async () => {
    if (!user) return;
    
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      const isUserAdmin = profile?.role === 'admin';
      setIsAdmin(isUserAdmin);
      
      if (isUserAdmin) {
        await loadStats();
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
      setError('Failed to verify admin access');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Get total users
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get active posts in last 7 days
      const { count: activePosts } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      // Get active users in last 7 days
      const { count: activeUsers } = await supabase
        .from('posts')
        .select('user_id', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .not('user_id', 'is', null);

      // Get pending reports
      const { count: pendingReports } = await supabase
        .from('reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      setStats({
        totalUsers: totalUsers || 0,
        activePosts: activePosts || 0,
        activeUsers: activeUsers || 0,
        pendingReports: pendingReports || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Failed to load statistics');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <button
          onClick={() => setActiveSection('settings')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="bg-royal-purple/10 p-3 rounded-lg">
              <Users className="w-6 h-6 text-royal-purple" />
            </div>
            <span className="text-sm font-medium text-green-600">+12%</span>
          </div>
          <p className="mt-4 text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
          <p className="text-gray-600">Total Users</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="bg-royal-purple/10 p-3 rounded-lg">
              <MessageSquare className="w-6 h-6 text-royal-purple" />
            </div>
            <span className="text-sm font-medium text-green-600">+23%</span>
          </div>
          <p className="mt-4 text-2xl font-semibold text-gray-900">{stats.activePosts}</p>
          <p className="text-gray-600">Active Posts</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="bg-royal-purple/10 p-3 rounded-lg">
              <Activity className="w-6 h-6 text-royal-purple" />
            </div>
            <span className="text-sm font-medium text-green-600">+5%</span>
          </div>
          <p className="mt-4 text-2xl font-semibold text-gray-900">{stats.activeUsers}</p>
          <p className="text-gray-600">Active Users</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="bg-royal-purple/10 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-royal-purple" />
            </div>
            <span className="text-sm font-medium text-red-600">+8%</span>
          </div>
          <p className="mt-4 text-2xl font-semibold text-gray-900">{stats.pendingReports}</p>
          <p className="text-gray-600">Pending Reports</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveSection('overview')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeSection === 'overview'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveSection('analytics')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeSection === 'analytics'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveSection('reports')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2",
              activeSection === 'reports'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            Reports
            {stats.pendingReports > 0 && (
              <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                {stats.pendingReports}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeSection === 'users'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            Users
          </button>
          <button
            onClick={() => setActiveSection('settings')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeSection === 'settings'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          <Analytics />
        </div>
      )}

      {activeSection === 'analytics' && (
        <Analytics />
      )}

      {activeSection === 'reports' && (
        <ReportsTable />
      )}

      {activeSection === 'users' && (
        <UserManagement />
      )}

      {activeSection === 'settings' && (
        <SystemSettings />
      )}
    </div>
  );
}